import { useEffect, useRef } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import {
  ORDER_PICK_SCAN_BROADCAST_EVENT,
  ORDER_PICK_SCANS_CLEARED_EVENT,
  orderPickScanRealtimeTopic,
  parseOrderPickScanMessage,
  parseOrderPickScanRemoval,
  type OrderPickScanLine,
} from '@/shared/utils/orderPickScan';
import { getSupabaseRealtimeClient } from '../services/supabaseRealtimeClient';

interface UseOrderPickScanRealtimeParams {
  readonly orderId: string;
  readonly enabled?: boolean;
  readonly onLines: (lines: readonly OrderPickScanLine[]) => void;
  readonly onCleared: () => void;
  readonly onLineRemoved: (lineId: string) => void;
}

function subscribeChannel(channel: RealtimeChannel, label: string): void {
  channel.subscribe((status: string) => {
    if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
      console.warn(`[order-pick-scans] ${label} channel error:`, status);
    }
  });
}

/**
 * Applies scan events for one order. Broadcast is its own channel so a
 * postgres_changes failure cannot drop the live push. Both paths are idempotent.
 * Nothing is subscribed when `enabled` is false or `orderId` is empty.
 */
export function useOrderPickScanRealtime({
  orderId,
  enabled = true,
  onLines,
  onCleared,
  onLineRemoved,
}: UseOrderPickScanRealtimeParams): void {
  const onLinesRef = useRef(onLines);
  const onClearedRef = useRef(onCleared);
  const onLineRemovedRef = useRef(onLineRemoved);

  useEffect(() => {
    onLinesRef.current = onLines;
  }, [onLines]);

  useEffect(() => {
    onClearedRef.current = onCleared;
  }, [onCleared]);

  useEffect(() => {
    onLineRemovedRef.current = onLineRemoved;
  }, [onLineRemoved]);

  useEffect(() => {
    if (!enabled || orderId.trim() === '') {
      return undefined;
    }

    const realtimeClient = getSupabaseRealtimeClient();
    if (!realtimeClient) {
      return undefined;
    }

    const topic = orderPickScanRealtimeTopic(orderId);
    const applyLines = (message: unknown): void => {
      const parsed = parseOrderPickScanMessage(message);
      if (parsed == null || parsed.orderId !== orderId) return;
      onLinesRef.current(parsed.lines);
    };

    const broadcastChannel: RealtimeChannel = realtimeClient.channel(topic, {
      config: { broadcast: { self: true } },
    });
    broadcastChannel.on('broadcast', { event: ORDER_PICK_SCAN_BROADCAST_EVENT }, applyLines);
    broadcastChannel.on('broadcast', { event: ORDER_PICK_SCANS_CLEARED_EVENT }, (message: unknown) => {
      const parsed = parseOrderPickScanRemoval(message);
      if (parsed == null || parsed.orderId !== orderId) return;
      onClearedRef.current();
    });
    subscribeChannel(broadcastChannel, 'broadcast');

    const changesChannel: RealtimeChannel = realtimeClient.channel(`${topic}-changes`);
    changesChannel.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'wms',
        table: 'order_pick_scans',
        filter: `order_id=eq.${orderId}`,
      },
      applyLines,
    );
    changesChannel.on(
      'postgres_changes',
      {
        event: 'DELETE',
        schema: 'wms',
        table: 'order_pick_scans',
        filter: `order_id=eq.${orderId}`,
      },
      (message: unknown) => {
        const parsed = parseOrderPickScanRemoval(message);
        if (parsed == null || parsed.orderId !== orderId) return;
        if (parsed.lineId == null) {
          onClearedRef.current();
          return;
        }
        onLineRemovedRef.current(parsed.lineId);
      },
    );
    subscribeChannel(changesChannel, 'postgres_changes');

    return () => {
      realtimeClient.removeChannel(broadcastChannel).catch(() => {
        // no-op cleanup
      });
      realtimeClient.removeChannel(changesChannel).catch(() => {
        // no-op cleanup
      });
    };
  }, [enabled, orderId]);
}
